import { ActionButton, FormInputRow, ImageUtil, Input } from '@emerald-works-nova/components'
import { useEvent, useEventsOnViewLoad } from '@emerald-works/react-event-bus-client'
import { Button, Divider, FormControlLabel, Grid, Radio, RadioGroup, Typography } from '@mui/material'
import classNames from 'classnames'
import React, { useState } from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { BannerStatic } from '../../../components'
import { CopyIcon } from '../../../components/icons'
import { TenantEditSlice } from '../../../reducers'
import { readFileText } from '../../../util/file-util'
import { InputColor, InputLogo } from '../components'
import { SamlAttributeInput } from '../components/saml-atttribute-input'
import { useTenantFormContext } from '../tenant-form-context'
import { useStyles } from './style'
import { AddCustomAttr } from './add-custom-attr'

const SamlTab = () => {
  const ref = React.useRef()
  const classes = useStyles()
  const dispatch = useDispatch()
  const { tenant, setIsLoading, setLoadingMsg, mainClasses } = useTenantFormContext()
  const form = useForm({ mode: 'onChange' })
  const { getValues, setValue, formState: { errors }, watch } = form

  const { loginProviders, region, cognitoSettings: { userPoolId, userPoolDomain }, pk } = tenant
  const acsUrl = React.useMemo(() => `https://${userPoolDomain}.auth.${region}.amazoncognito.com/saml2/idpresponse`, [userPoolDomain, region])
  const entityId = React.useMemo(() => `urn:amazon:cognito:sp:${userPoolId}`, [userPoolId])
  const textToCopy = React.useMemo(() => `${acsUrl}\n${entityId}`, [acsUrl, entityId])
  const provider = React.useMemo(() => loginProviders?.find(p => p.providerType === 'SAML' && p.providerName !== 'internal'), [loginProviders])
  const [radioHasSaml, setRadioHasSaml] = React.useState(provider?.samlMetadata ? 'yes' : 'no')

  const [getCustomAttributeList] = useEvent([
    {
      eventName: 'tenantGetSSOCustomAttributes'
    }
  ])

  const [tenantUpdateSso] = useEvent([{
    eventName: 'tenantUpdateSso',
    onSuccess: (tenant) => {
      setIsLoading(false)
      BannerStatic.show({ label: 'SSO configuration updated' })
      dispatch(TenantEditSlice.actions.updateTenantLoginProviders(tenant.loginProviders))
    }
  }])
  useEventsOnViewLoad(() => {
    tenantUpdateSso.registerAdhocOnErrorListener((error) => {
      console.error('tenantUpdateSso.registerAdhocOnErrorListener', error)
      setIsLoading(false)
      BannerStatic.show({ label: `There was an error: ${error.message}`, isError: true })
    })
  }, [tenantUpdateSso, setIsLoading])

  // initialize the form fields
  React.useEffect(() => {
    if (!provider) return
    Object.entries(provider).forEach(([key, value]) => setValue(key, value))
  }, [provider, setValue])

  const onCopy = React.useCallback(() => {
    BannerStatic.show({
      label: 'Content copied',
      anchorOrigin: { vertical: 'bottom', horizontal: 'center' }
    })
  }, [])
  const handleSave = React.useCallback(() => {
    tenantUpdateSso.trigger({ pk, provider: getValues() })
    setLoadingMsg('Updating SSO configuration')
  }, [getValues, tenantUpdateSso, setLoadingMsg, pk])

  React.useEffect(() => { setIsLoading(tenantUpdateSso.isWorking) }, [setIsLoading, tenantUpdateSso.isWorking])

  const fileInputRef = React.useRef()
  const onChooseFile = React.useCallback(async (file) => {
    const content = await readFileText(file)
    setValue('samlMetadata', content)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [setValue, fileInputRef])

  // Getting XML Saml Metadata parsing it to a var, watching the changes to upload the autofill on Attribute Mapping forms
  const [attributesFromSaml, setAttributesFromSaml] = useState([])
  const samlMetadata = watch('samlMetadata')
  React.useEffect(() => extractFromSamlmetadata(samlMetadata), [samlMetadata])

  const extractFromSamlmetadata = (content) => {
    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(content, 'text/xml')
    let attributeElements = xmlDoc.getElementsByTagName('Attribute')
    if (attributeElements.length === 0) {
      attributeElements = xmlDoc.getElementsByTagName('auth:ClaimType')
    }
    const updateAttr = []
    for (const attributeElement of attributeElements) {
      let attributeName

      if (attributeElement.getAttribute('Name')) {
        attributeName = attributeElement.getAttribute('Name')
      } else if (attributeElement.getAttribute('Uri')) {
        attributeName = attributeElement.getAttribute('Uri')
      }

      if (attributeName && !updateAttr.includes(attributeName)) {
        updateAttr.push(attributeName)
      }
    }

    setAttributesFromSaml(updateAttr)
  }

  const clearSamlMetadata = () => {
    setValue('samlMetadata', '')
  }
  const [openAddCustomAttr, setOpenAddCustomAttr] = React.useState(false)
  const providerLogo = watch('providerLogo')
  const providerButtonLabel = watch('buttonLabel')
  const providerLogoAlt = watch('logoAlt', '')
  const providerName = watch('name', 'SSO')
  const buttonColor = watch('buttonColor')

  const handleCancelCatModal = () => {
    setOpenAddCustomAttr(false)
  }
  const onOpenAddCustomAttr = () => {
    setOpenAddCustomAttr(true)
  }
  const getIcon = (providerLogo) => {
    if (!providerLogo) return null
    return (
      <img
        className={classes.providerLogo}
        src={ImageUtil.ImgixGenerateImageUrl(providerLogo.s3Url, providerLogo.imgixCropParams)}
      />
    )
  }

  const isLoading = getCustomAttributeList.isWorking

  return (
    <Grid className={classes.root}>
      <AddCustomAttr
        open={openAddCustomAttr}
        onClose={handleCancelCatModal}
        onCancel={handleCancelCatModal}
        getCustomAttributeList={getCustomAttributeList}
      />
      <Grid container alignItems='center' className={classNames(mainClasses.ctnTitle, classes.ctnTitle)}>
        <Grid item xs={8}>
          <Typography variant='h6'>Single Sign On</Typography>
        </Grid>
        <Grid container item justifyContent='flex-end' xs={4}>
          {radioHasSaml === 'yes' && (
            <ActionButton
              variant='primary'
              dataTest='button-save-tenant-sso'
              onClick={handleSave}
            > Save Changes
            </ActionButton>
          )}
        </Grid>
      </Grid>
      <Typography gutterBottom variant='body1'><strong>Configuration</strong></Typography>
      <Typography gutterBottom color='textSecondary' variant='body1'>Copy our live metadata information below to share with the client.</Typography>
      <Grid className={classes.configCtn}>
        <Grid container justifyContent='flex-end'>
          <CopyToClipboard text={textToCopy} onCopy={onCopy}>
            <Button
              variant='outlined'
              data-test='copy-acs-entity-button'
              className={classes.btnCopy}
              endIcon={<CopyIcon />}
            > Copy Snippet
            </Button>
          </CopyToClipboard>
        </Grid>
        <Grid>
          <Typography color='textSecondary' variant='body1'>{acsUrl}</Typography>
          <Typography color='textSecondary' variant='body1'>{entityId}</Typography>
        </Grid>
      </Grid>

      <Divider style={{ marginBottom: 16 }} />

      <Typography gutterBottom variant='body1'><strong>Do you have the client&apos;s metadata to import?</strong></Typography>

      <RadioGroup aria-label='Has SAML metadata file?' name='hasSaml' value={radioHasSaml} onChange={({ target }) => setRadioHasSaml(target.value)}>
        <Grid container>
          <Grid item xs={6}>
            <FormControlLabel value='no' control={<Radio color='primary' />} label="No, I don't have SAML Metadata file/content yet" />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel value='yes' control={<Radio color='primary' />} label='Yes, I have SAML Metadata file/content' />
          </Grid>
        </Grid>
      </RadioGroup>
      <br />

      {radioHasSaml === 'yes' && (
        <Grid container>
          <Typography variant='h6'><strong>SSO Settings</strong></Typography>

          <Grid item xs={12} className={classes.titleBold}>
            <FormInputRow
              title='Provider Name'
              smallFormTitles
              description='This is the name of the identity provider. It will be shown to your users in the login button e.g (Azure, Microsoft, Google, AD)'
              InputComponent={
                <Input
                  autoFocus
                  rules={{ required: true }}
                  label='Provider Name'
                  name='name'
                  control={form.control}
                  valid={errors.providerName === undefined}
                  error='Provider Name is required'
                  showErrors
                />
              }
            />
          </Grid>
          <Grid item xs={12} className={classes.titleBold}>
            <FormInputRow
              title='SAML Metadata'
              smallFormTitles
              description='This is the metada configuration file that your identity provider will give you.'
              InputComponent={(
                <>
                  <Input
                    label='SAML Metadata'
                    name='samlMetadata'
                    control={form.control}
                    valid={errors.samlMetadata === undefined}
                    error='SAML Metadata is required'
                    showErrors
                    multiline
                    rowsMax={16}
                    rows={4}
                  />
                  <Grid item xs={12} className={classes.samlFilebtnGroup}>
                    <Button
                      variant='contained'
                      color='primary'
                      component='label'
                      onClick={() => setOpenAddCustomAttr(true)}
                    >
                      Import from file
                      <input ref={fileInputRef} type='file' onChange={({ target }) => onChooseFile(target.files[0])} hidden />
                    </Button>
                    <Button
                      variant='outlined'
                      color='primary'
                      disabled={samlMetadata === ''}
                      onClick={() => ref.current.fillAttrMetadata()}
                    >
                      Autofill Attribute Mapping
                    </Button>
                    <Button
                      variant='text'
                      color='primary'
                      onClick={clearSamlMetadata}
                    >
                      Clear Data
                    </Button>
                  </Grid>
                </>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography className={classes.attrMap} variant='body1' data-test='basic-info'><strong>Attribute Mapping</strong></Typography>
            <Grid container style={{ flexDirection: 'column' }}>
              <Grid style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Grid style={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant='caption'><strong>Map attributes between your provider and your user pool.</strong></Typography>
                  <Typography variant='caption'>Your required attributes are mapped to the equivalent attributes. Each attribute you add must be mapped to a attribute.</Typography>
                </Grid>
                <Grid>
                  <Button
                    variant='contained'
                    color='primary'
                    component='label'
                    onClick={() => onOpenAddCustomAttr()}
                  >
                    Add Custom Attribute
                  </Button>
                </Grid>
              </Grid>
              <SamlAttributeInput disableBtnAutofill={samlMetadata === ''} ref={ref} attributeMapList={attributesFromSaml} provider={provider} setValue={setValue} getCustomAttributeList={getCustomAttributeList} isLoading={isLoading} />
            </Grid>
          </Grid>

          <Typography variant='h6'><strong>Button Settings (Optional)</strong></Typography>
          <Grid item xs={12} className={classes.titleBold}>
            <FormInputRow
              title='Button Label'
              smallFormTitles
              description='This is the sign in button label, it will prefix the "Provider Name" value.'
              InputComponent={<Input label='Button Label' name='buttonLabel' control={form.control} />}
            />
          </Grid>
          <Grid item xs={12} className={classes.titleBold}>
            <FormInputRow
              title='Button Logo'
              smallFormTitles
              description='This is style of the colour button. Selecting coloured, the colour used will be that of the branding.'
              InputComponent={(
                <InputLogo
                  name='providerLogo'
                  minWidth={30}
                  minHeight={30}
                  maxSize={1048576}
                  {...form}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} className={classes.titleBold}>
            <FormInputRow
              title='Button Colour Style'
              smallFormTitles
              description='This is style of the colour button. Selecting coloured, the colour used will be that of the branding.'
              InputComponent={
                <InputColor
                  {...form}
                  errors={errors}
                  name='buttonColor'
                  defaultValue={tenant?.mainColor || '#E0E0E0'}
                />
              }
            />
          </Grid>

          {/* <Grid item xs={12}>
            <FormInputRow
              title='Logo Alt'
              description="The logo's Alt attribute."
              InputComponent={
                <Input
                  label='Logo Alt'
                  name='logoAlt'
                  control={form.control}
                />
              }
            />
          </Grid> */}
          <Grid item container justifyContent='center' xs={12}>
            <Grid item xs={12} className={classes.titleBold}>
              <Typography variant='body1' className={classes.titlePreview}>Login Button Preview</Typography>
            </Grid>
            <Grid container direction='row' alignItems='flex-start'>
              <Grid item className={classes.descriptionWrapper}>
                <Typography variant='caption' color='textSecondary'>Check the button preview to the side.</Typography>
              </Grid>
              <Grid item container justifyContent='center' className={classes.inputWrapper}>
                <Button
                  variant='contained'
                  classes={{ label: classes.providerLbl }}
                  startIcon={getIcon(providerLogo)}
                  style={{ background: buttonColor }}
                  alt={providerLogoAlt}
                >
                  {`${providerButtonLabel || 'Sign in with'} ${providerName}`}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  )
}

export default SamlTab
